import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-record-header>` says about itself.
 *
 * The thing to know before reading the declarations: this piece PUTS THE OBJECT TYPE ABOVE THE
 * RECORD NAME, and the object type is the larger of the two. That inversion is deliberate and it is
 * what makes this a record header rather than a page header with an icon — you learn WHAT you are
 * looking at first and WHICH ONE second. The record name is the one set in the accent.
 *
 * The top stripe used to read `--caos-color-record-header-stripe`, a token that NAMED THIS
 * COMPONENT — the only one in the vocabulary a brand could move solely by knowing this component
 * exists. This comment was the only place that was written down — nothing asserted it — which is
 * how the token survived CAOS-586, the ticket that removed two others for naming components.
 * CAOS-1680 removed it (its value was `var(--caos-color-accent)` in the base and in every brand
 * that set it, so no brand lost a distinction it was using) and put a check behind the rule: no
 * token declaration in tokens.css may name a catalogued component, derived from the catalogue
 * rather than from a list of names. The stripe now reads the accent, like every other colour here.
 *
 * There is NO default slot. Everything that goes inside is addressed by name — icon, status, fields,
 * actions — so content dropped in without a `slot` attribute is not drawn at all.
 */
const docs: ComponentDocs = {
  aliases: ['record banner', 'highlights panel', 'record highlights', 'detail header', 'record title bar'],

  answers: [
    'put a banner at the top of a record',
    'say which object this record is and what it is called',
    'show the status of a record next to its name',
    'say in a few words which record this is, under its name',
    'show a few key fields at the top of a record without opening it',
    'put Edit and Delete at the top right of a record',
  ],

  // One record, one surface. It is not a list head — that is PageHeader — so no list surfaces here.
  placements: ['recordPage'],

  inputs: [
    {
      name: 'objectLabel',
      attribute: 'object-label',
      type: 'text',
      description:
        'The object type — Order, Account, Case. Drawn as the DOMINANT heading above the record ' +
        'name. Hidden when empty.',
    },
    {
      name: 'heading',
      attribute: 'heading',
      type: 'text',
      description:
        'The record’s own name, drawn beneath the object type in the accent colour. This is the <h1>, ' +
        'so it is also the surface’s accessible heading.',
      required: true,
      // The record's own name field, not text an author types once when placing the header
      // (CAOS-562) — the docs above say so directly.
      source: 'recordField',
    },
    {
      name: 'sub',
      attribute: 'sub',
      type: 'text',
      // No customer's business vocabulary in the examples — `vocabulary.test.ts` scans this file,
      // and the first draft of this description named three of a customer's own objects, which is
      // exactly the leak it exists to catch. (So did the comment that had been written here to
      // explain the fix, by repeating one of the words inside it.) Person, account and case carry
      // the idea without naming anybody's objects.
      description:
        'A quiet second line under the record’s name — what this one is, in a few words: a person’s ' +
        'job title, an account’s industry, the owner a case is assigned to. Hidden when empty, so a ' +
        'record with nothing to say here shows no blank line.',
      // The record's own field, like `heading` above — the page names WHICH field, and the value
      // comes from the record being shown, never from text somebody typed when placing the header.
      source: 'recordField',
    },
    {
      name: 'noIcon',
      attribute: 'no-icon',
      type: 'boolean',
      description:
        'Drop the object-icon tile entirely, leaving no gap where it was. For a band that identifies ' +
        'the record by words alone. The icon slot still exists for anything that opts back in.',
      default: false,
    },
  ],

  // No listeners, no dispatch. Whatever is slotted into status, fields or actions raises its own
  // events; this piece only arranges them.
  events: [],

  state: [],

  slots: [
    {
      name: 'icon',
      takes:
        'The object’s mark, inside a 44px accent tile. Falls back to a neutral document glyph when ' +
        'nothing is supplied, and is suppressed entirely by no-icon.',
    },
    { name: 'status', takes: 'Inline beside the record name — a StatusPill, a Tag, or several of them.' },
    { name: 'fields', takes: 'The key read-only fields under the name. KeyValueRows, or a DetailSection.' },
    { name: 'actions', takes: 'The record-level controls, pinned right. Drops to its own full-width row under 560px.' },
  ],

  tokens: [
    { token: '--caos-color-surface', controls: 'The fill of the card.' },
    { token: '--caos-color-border-strong', controls: 'The card’s border on the other three edges.' },
    { token: '--caos-shadow-card', controls: 'The card’s elevation.' },
    { token: '--caos-radius-lg', controls: 'The corner radius of the card.' },
    {
      token: '--caos-color-accent',
      controls: 'The ink of the record name, and the 3px stripe across the top of the card.',
    },
    { token: '--caos-color-accent-soft', controls: 'The fill of the object-icon tile.' },
    { token: '--caos-color-accent-strong', controls: 'The glyph inside the object-icon tile.' },
    { token: '--caos-font-display', controls: 'The typeface of both the object type and the record name.' },
  ],

  parts: [
    { part: 'header', is: 'the header block' },
    { part: 'icon', is: 'the object glyph' },
    { part: 'object-label', is: 'the object name above the title' },
    { part: 'title', is: 'the record name' },
    { part: 'status', is: 'the area beside the title a pill is slotted into' },
    { part: 'sub', is: 'the quiet second line under the record name' },
    { part: 'fields', is: 'the row of highlight fields' },
    { part: 'actions', is: 'the action group at the right' },
  ],

  methods: [],

  placing:
    'First item on a record surface, above the sections that show the record’s fields. It draws itself ' +
    'as a card — border, radius, shadow and an accent stripe along the top — so it needs no wrapper ' +
    'around it and should not be nested inside another card. Under 560px the header wraps and the ' +
    'actions take their own full-width row, so it does not need a separate narrow layout.',

  whenToUse:
    'The top of a surface that is about ONE record, where a person has to see what kind of thing it is, ' +
    'which one it is, what state it is in, and what they can do to it, without reading further down.',

  insteadUse: [
    {
      name: 'PageHeader',
      when:
        'The surface is a LIST or an overview rather than one record. That one has an eyebrow, a record ' +
        'count and a description; it has no object mark, no status line and no field band, because a ' +
        'set of records has none of those.',
    },
    {
      name: 'DetailSection',
      when: 'You are heading a block of fields further down the record rather than the record itself.',
    },
    {
      name: 'Inspector',
      when:
        'The record is being shown beside something else in a side panel rather than being the surface. ' +
        'That one brings its own heading and close control.',
    },
    {
      name: 'StatusPill',
      when: 'All you need is the state badge. It goes INSIDE this piece’s status slot rather than instead of it.',
    },
  ],

  accessibility:
    'The record name is a real <h1>, so the surface has a heading without the author adding one, and the ' +
    'icon tile is marked `aria-hidden` so a decorative object mark is not read out. The status, fields ' +
    'and actions regions vanish from the layout when empty, so an unused slot announces nothing. Left to ' +
    'the author: the object type is drawn as plain text rather than as a heading element, so it is read ' +
    'as part of the banner and not as a level in the document outline — if the object type needs to be ' +
    'announced first, it belongs in the heading text. Icon-only action controls still need names.',

  examples: [
    {
      key: 'record',
      label: 'A record',
      summary: 'The two text inputs on their own — the least a record header needs to identify a record.',
      placement: {
        component: 'caos-record-header',
        inputs: { 'object-label': 'Order', heading: 'ORD-4471 — Meridian Supply' },
      },
    },
    {
      key: 'full',
      label: 'Status, key fields and actions',
      summary:
        'Every named slot filled — the shape a real record page uses, where the state and the two or ' +
        'three fields that matter are readable without scrolling.',
      placement: {
        component: 'caos-record-header',
        inputs: { 'object-label': 'Order', heading: 'ORD-4471 — Meridian Supply' },
        children: [
          { component: 'caos-status-pill', inputs: { slot: 'status', intent: 'info' }, children: ['In review'] },
          { component: 'caos-tag', inputs: { slot: 'status' }, children: ['Rush'] },
          { component: 'caos-key-value-row', inputs: { slot: 'fields', label: 'Account', value: 'Meridian Supply' } },
          { component: 'caos-key-value-row', inputs: { slot: 'fields', label: 'Total', value: '$12,400' } },
          { component: 'caos-button', inputs: { slot: 'actions', variant: 'secondary' }, children: ['Clone'] },
          { component: 'caos-button', inputs: { slot: 'actions', variant: 'primary' }, children: ['Submit'] },
        ],
      },
    },
    {
      key: 'no-icon',
      label: 'Without the object mark',
      summary:
        'The same header with the icon tile suppressed — it is removed from the flow rather than blanked, ' +
        'so the text starts at the card edge with no gap where the tile was.',
      placement: {
        component: 'caos-record-header',
        inputs: { 'object-label': 'Account', heading: 'Meridian Supply', 'no-icon': true },
        children: [
          { component: 'caos-status-pill', inputs: { slot: 'status', intent: 'success' }, children: ['Customer'] },
          { component: 'caos-key-value-row', inputs: { slot: 'fields', label: 'Owner', value: 'Dana Whitfield' } },
        ],
      },
    },
  ],
};

export default docs;
